import React, { useMemo } from 'react';
import { ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { useMount } from '@shared/utils/hooks/useMount';
import { AllyTransferPromo } from '@shared/components/TransferToBankDialog/AllyTransferPromo';
import { AllyRepeatPromo } from '@shared/components/TransferToBankDialog/AllyRepeatPromo';
import { RevolutTransferPromo } from '@shared/components/TransferToBankDialog/RevolutTransferPromo';
import { getTransferPartnerConfig } from '@shared/components/TransferToBankDialog/transferPartnerConfigs';
import { TRANSFER_FEE } from '@shared/components/TransferToBankDialog/constants';
import type { PayoutAccountInfo } from './PayoutDialog.types';

const ALLY_BONUS = 200; // 200 minor units = $2 USD bonus credit on top of the transfer amount
const MIN_AMOUNT_IN_MINOR_UNITS = 200;

type ConnectBankStepProps = Readonly<{
  payoutAccountInfo: PayoutAccountInfo;
  onConnectBank: () => void;
  onBankPromoViewed?: (partner: string) => void;
  onBankPromoInteracted?: (cta: string, partner: string) => void;
}>;

export const ConnectBankStep: React.FC<ConnectBankStepProps> = ({ payoutAccountInfo, onConnectBank, onBankPromoViewed, onBankPromoInteracted }) => {
  const { tq } = useTranslation('cashFundsAdmin');
  const { formatCurrency } = useCurrencyFormatter();
  const { value: activePartner } = useFeatureValue('activeCashFundPartner');
  const { value: transferPartner } = useFeatureValue('enableCashFundPartnerTransferToBank');
  const { value: repeatTransferPartner } = useFeatureValue('enableCashFundPartnerTransferToBankRepeat');

  const isReadyToUse = payoutAccountInfo.status === 'readyToUse';
  const available = payoutAccountInfo.balance?.available;
  const currencyCode = available?.currencyCode ?? payoutAccountInfo.currencyCode;
  const isPayoutAllowed = (available?.valueInMinorUnits ?? 0) > MIN_AMOUNT_IN_MINOR_UNITS;

  const activeTransferConfig = activePartner && activePartner === transferPartner ? getTransferPartnerConfig(activePartner) : null;
  const activeRepeatConfig = activePartner && activePartner === repeatTransferPartner ? getTransferPartnerConfig(activePartner) : null;

  const showAllyRepeatPromo = isReadyToUse && activeRepeatConfig?.partnerId === 'ally' && isPayoutAllowed;
  const showRevolutRepeatPromo = isReadyToUse && activeRepeatConfig?.partnerId === 'revolut' && isPayoutAllowed;
  const showAllyInitialPromo = !isReadyToUse && activeTransferConfig?.partnerId === 'ally';
  const showRevolutInitialPromo = !isReadyToUse && activeTransferConfig?.partnerId === 'revolut';

  const { formattedBalance, feeFormatted, totalFormatted, allyAmountFormatted } = useMemo(() => {
    const balance = available?.valueInMajorUnits ?? 0;
    const totalTransferValue = balance > 0 ? balance - TRANSFER_FEE : 0;
    const allyTransferValue = totalTransferValue + ALLY_BONUS;
    return {
      formattedBalance: formatCurrency({ priceFloatingPointDecimalString: balance.toString(), priceCurrencyCode: currencyCode, formatForm: 'auto' }),
      feeFormatted: formatCurrency({ priceFloatingPointDecimalString: TRANSFER_FEE.toString(), priceCurrencyCode: currencyCode, formatForm: 'auto' }),
      totalFormatted: formatCurrency({ priceFloatingPointDecimalString: totalTransferValue.toString(), priceCurrencyCode: currencyCode, formatForm: 'auto' }),
      allyAmountFormatted: formatCurrency({ priceFloatingPointDecimalString: allyTransferValue.toString(), priceCurrencyCode: currencyCode, formatForm: 'auto' }),
    };
  }, [formatCurrency, available, currencyCode]);

  useMount(() => {
    if (showAllyRepeatPromo || showAllyInitialPromo) onBankPromoViewed?.('ally');
    else if (showRevolutRepeatPromo || showRevolutInitialPromo) onBankPromoViewed?.('revolut');
  });

  if (showAllyRepeatPromo) {
    return (
      <AllyRepeatPromo
        allyAmountFormatted={allyAmountFormatted}
        standardAmountFormatted={totalFormatted}
        feeFormatted={feeFormatted}
        onAcknowledge={onConnectBank}
        onBankPromoInteracted={onBankPromoInteracted}
      />
    );
  }

  if (showRevolutRepeatPromo && activeRepeatConfig) {
    return (
      <RevolutTransferPromo
        config={activeRepeatConfig}
        standardAmountFormatted={formattedBalance}
        onSkip={onConnectBank}
        onBankPromoInteracted={onBankPromoInteracted}
      />
    );
  }

  if (showRevolutInitialPromo && activeTransferConfig) {
    return (
      <RevolutTransferPromo
        config={activeTransferConfig}
        standardAmountFormatted={formattedBalance}
        onSkip={onConnectBank}
        onBankPromoInteracted={onBankPromoInteracted}
      />
    );
  }

  if (showAllyInitialPromo) {
    return (
      <AllyTransferPromo
        allyAmountFormatted={allyAmountFormatted}
        standardAmountFormatted={totalFormatted}
        feeFormatted={feeFormatted}
        onConnectBank={onConnectBank}
        onBankPromoInteracted={onBankPromoInteracted}
      />
    );
  }

  return (
    <>
      <Flex
        justifyContent="center"
        marginBottom={8}
      >
        <TextV2 typographyVariant="display1">{tq`Transfer to Bank`}</TextV2>
      </Flex>
      <Flex
        flexDirection="column"
        alignItems="center"
        gap={1}
        marginBottom={8}
      >
        <TextV2
          typographyVariant="display2"
          color="mono14"
        >
          {formattedBalance}
        </TextV2>
        <TextV2
          typographyVariant="hed1"
          color="mono14"
        >
          {tq`Transferrable Balance`}
        </TextV2>
      </Flex>
      <ButtonV2
        size="lg"
        shape="rounded"
        intent="neutral"
        fullWidth
        onClick={onConnectBank}
      >
        {tq`Connect Bank Account`}
      </ButtonV2>
    </>
  );
};
